import React, {useEffect, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {API_ENDPOINTS} from "../apiConfig";
import API from "../utils/API";
import Error from "./Error";
import Forms from "./Forms";
import {useTranslation} from 'react-i18next';
import RepairSelector from "./RepairSelector";
import "./DeviceModal.css";

const DeviceModal = ({close, onDeviceUpdate, device}) => {
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [category, setCategory] = useState();
    const [repairList, setRepairList] = useState([]);
    const [sparePartCatalog, setSparePartCatalog] = useState([]);
    const [image, setImage] = useState(null);
    const [categories, setCategories] = useState([]);
    const [repairCatalog, setRepairCatalog] = useState([]);
    const [diagnosticCatalog, setDiagnosticCatalog] = useState([]);
    const [imagePath, setImagePath] = useState(null);
    const [deleteImage, setDeleteImage] = useState(false);
    const [availableColors, setAvailableColors] = useState([]);
    const [availableStorage, setAvailableStorage] = useState([]);
    const [diagnosticCheckedIds, setDiagnosticCheckedIds] = useState([]);
    const [buybackParts, setBuybackParts] = useState([]);
    const inputRef = useRef(null);

    const [error, setError] = useState('');
    const {t} = useTranslation();

    useEffect(() => {
        setTimeout(() => {
            inputRef.current.focus();
        }, 1);
    }, []);

    useEffect(() => {
        setName(device ? device.name : '');
        setCode(device ? device.code : '');
        setImagePath(device ? device.image : null);
        setAvailableColors(toSelectMultiValue(device?.availableColors || []));
        setAvailableStorage(toStorageSelectMultiValue(device?.availableStorage || []));
        setCategory(device?.category.length ? {value: device.category[0]._id, label: device.category[0].name} : null);
        setRepairList(device?.repairList.map(repair => ({
            price: repair.price,
            repair: repair.repair._id,
            active: repair.active,
            timeRequirement: repair.timeRequirement
        })));
        setDiagnosticCheckedIds((device?.diagnosticItemList || [])
            .filter((item) => item?.active !== false)
            .map((item) => item.item?._id || item.item)
            .filter(Boolean));
        setBuybackParts(normalizeBuybackParts(device?.buybackParts));
    }, [device]);

    useEffect(() => {
        const fetchCategories = async () => {
            const response = await API.get(API_ENDPOINTS.device.listCategories);
            setCategories(response.data);
        };
        const fetchRepairs = async () => {
            const response = await API.get(API_ENDPOINTS.repairCatalog.list);
            setRepairCatalog(response.data);
        };
        const fetchSpareParts = async () => {
            const response = await API.get(API_ENDPOINTS.part.list);
            setSparePartCatalog((response.data || []).filter((item) => item?.type === 'sparePart'));
        };
        const fetchDiagnosticCatalog = async () => {
            const response = await API.get(API_ENDPOINTS.diagnosticCatalog.list);
            setDiagnosticCatalog(response.data || []);
        };

        fetchCategories();
        fetchRepairs();
        fetchSpareParts();
        fetchDiagnosticCatalog();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError();

        const formData = new FormData();
        formData.append('name', name);
        formData.append('code', code);
        category && formData.append('category', category.value);
        image && formData.append('image', image);
        formData.append('deleteImage', deleteImage);
        formData.append('availableColors', JSON.stringify(selectValuesToStrings(availableColors)));
        formData.append('availableStorage', JSON.stringify(selectValuesToStorageOptions(availableStorage)));
        repairList && formData.append('repairList', JSON.stringify(repairList));
        const legacySparePartIds = (device?.sparePartList || [])
            .filter((item) => item?.active !== false)
            .map((item) => item?.part?._id || item?.part)
            .filter(Boolean);
        formData.append('sparePartList', JSON.stringify(legacySparePartIds.map((id) => ({
            part: id,
            pricePct: 100,
            active: true
        }))));
        formData.append('buybackParts', JSON.stringify(buybackParts));
        const selectedDiagnosticIds = diagnosticCheckedIds.length
            ? diagnosticCheckedIds
            : diagnosticCatalog.map((item) => item._id);
        formData.append('diagnosticItemList', JSON.stringify(selectedDiagnosticIds.map((id) => ({
            item: id,
            active: true
        }))));

        try {
            if (device) {
                await API.put(`${API_ENDPOINTS.device.base}/${device._id}`, formData);
            } else {
                await API.post(API_ENDPOINTS.device.base, formData);
            }

            onDeviceUpdate();
            handleClose();
        } catch (error) {
            setError(error);
            console.error("Failed to update device:", error);
        }
    };

    const handleClose = () => {
        setImage(null);
        setDeleteImage(false);
        close();
    }

    const onRepairsChange = (updated) => {
        setRepairList(updated.map(({_id, price, active, timeRequirement}) => ({
            repair: _id, price, active, timeRequirement
        })));
    }

    const toggleDiagnosticItem = (itemId, nextChecked) => {
        const normalizedId = String(itemId);
        setDiagnosticCheckedIds((prev) => {
            const allIds = diagnosticCatalog.map((item) => String(item._id));
            const prevNormalized = prev.map((id) => String(id));

            if (!prevNormalized.length) {
                if (nextChecked) {
                    return [];
                }
                return allIds.filter((id) => id !== normalizedId);
            }

            if (nextChecked) {
                if (prevNormalized.includes(normalizedId)) {
                    return prev;
                }
                return [...prev, normalizedId];
            }

            return prevNormalized.filter((id) => id !== normalizedId);
        });
    };

    const compatibleSpareParts = sparePartCatalog
        .filter((item) => {
            const currentDeviceId = String(device?._id || '');
            if (!currentDeviceId) return true;
            const compatibleIds = (item?.compatibleWith || []).map((it) => String(it?._id || it));
            return compatibleIds.includes(currentDeviceId);
        })
        .map((item) => ({id: String(item._id), name: item.name}));

    const compatibleMap = new Map(compatibleSpareParts.map((item) => [item.id, item]));

    const getAlreadyUsedPartIds = () => new Set(
        buybackParts.map((id) => String(id))
    );

    const addPartById = (partId) => {
        const normalizedId = String(partId || '');
        if (!normalizedId) return;
        const used = getAlreadyUsedPartIds();
        const target = compatibleSpareParts.find((item) => item.id === normalizedId && !used.has(item.id));
        if (!target) return;
        setBuybackParts((prev) => [...prev, target.id]);
    };

    const removeBuybackPartRow = (index) => {
        setBuybackParts((prev) => prev.filter((_, i) => i !== index));
    };

    const renderImage = () => {
        if (imagePath) {
            const isAbsoluteUrl = /^https?:\/\//i.test(imagePath);
            const isPublicPath = String(imagePath).startsWith('/');
            const resolvedImageUrl = isAbsoluteUrl
                ? imagePath
                : isPublicPath
                    ? `${process.env.REACT_APP_IMAGE_URL}${imagePath}`
                    : `${process.env.REACT_APP_IMAGE_URL}/public/device-images/${imagePath}`;

            return <div>
                <Forms.Checkbox name="deleteImage" value={deleteImage} label={t('deleteImage')}
                                onChange={() => setDeleteImage(!deleteImage)}/>
                <img className="preview-image"
                     src={resolvedImageUrl}
                     alt=""/>
            </div>
        }
    }

    return (<form onSubmit={handleSubmit}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
                {device ? t('updateDevice') : t('addNewDevice')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Text
                    ref={inputRef}
                    required
                    placeholder="Název"
                    value={name}
                    onChange={(val) => setName(val)}
                    className="rounded-md"
                />
                <Forms.Text
                    placeholder="Kód"
                    value={code}
                    onChange={(val) => setCode(val)}
                    className="rounded-md"
                />
                <Forms.Select
                    required
                    placeholder="Kategorie"
                    value={category}
                    options={categories.map(category => ({
                        value: category._id, label: category.name
                    }))}
                    onChange={(val) => setCategory(val)}
                    className="rounded-md"
                />
                <Forms.File
                    placeholder="Obrázek"
                    onChange={e => setImage(e.target.files[0])}
                    className="rounded-md"
                />
                <Forms.Select
                    label="Dostupné barvy"
                    value={availableColors}
                    onChange={(nextValue, actionMeta) => setAvailableColors((prev) =>
                        updateMultiSelectOptions(prev, nextValue, actionMeta, normalizeColorOptionList)
                    )}
                    options={availableColors}
                    placeholder="Napište barvu a stiskněte Enter"
                    multiple
                    creatable
                />
                <Forms.Select
                    label="Dostupné kapacity"
                    value={availableStorage}
                    onChange={(nextValue, actionMeta) => setAvailableStorage((prev) =>
                        updateMultiSelectOptions(prev, nextValue, actionMeta, normalizeStorageOptionList)
                    )}
                    options={availableStorage}
                    placeholder="Napište např. 128 GB nebo 1 TB a stiskněte Enter"
                    multiple
                    creatable
                />
                {renderImage()}
            </div>
            <div className="mb-4">
                <RepairSelector
                    label="Dostupné opravy"
                    placeholder={t('DeviceModal.availableRepairs')}
                    repairCatalog={repairCatalog}
                    onRepairsChange={onRepairsChange}
                    repairList={repairList}
                />
            </div>
            <div className="mb-4">
                <div className="text-sm font-medium mb-2">Díly ke kontrole při výkupu donor zařízení</div>
                <div className="flex gap-2 mb-3 items-end">
                    <div className="flex-1">
                        <Forms.Select
                            placeholder="Vyberte kompatibilní díl"
                            options={compatibleSpareParts
                                .filter((item) => !getAlreadyUsedPartIds().has(item.id))
                                .map((item) => ({value: item.id, label: item.name}))}
                            value={null}
                            onChange={(option) => addPartById(option?.value)}
                        />
                    </div>
                </div>
                <div className="space-y-2">
                    {buybackParts.map((partId, idx) => {
                        const title = compatibleMap.get(String(partId))?.name || 'Díl';
                        return (
                            <div key={`${partId}_${idx}`} className="flex items-center justify-between border rounded px-3 py-2">
                                <div className="text-sm">{title}</div>
                                <div className="flex gap-2">
                                    <Forms.Button
                                        label="Odebrat"
                                        className="bg-red-700 hover:bg-red-800"
                                        onClick={() => removeBuybackPartRow(idx)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="mb-4">
                <div className="text-sm font-medium mb-2">Položky diagnostiky modelu</div>
                <div className="grid grid-cols-2 gap-2">
                    {diagnosticCatalog.map((item) => {
                        const normalizedId = String(item._id);
                        const checked = diagnosticCheckedIds.length === 0
                            ? true
                            : diagnosticCheckedIds.map((id) => String(id)).includes(normalizedId);
                        return (
                            <Forms.Checkbox
                                key={item._id}
                                label={`${item.name}`}
                                value={checked}
                                onChange={(nextValue) => toggleDiagnosticItem(item._id, nextValue)}
                            />
                        );
                    })}
                </div>
            </div>

            {error && <Error error={error}/>}
            <Forms.Button
                submit
                label={device ? t('update') : t('save')}
                className="mt-4"
            />
        </form>

    );
};

function toSelectMultiValue(values) {
    return normalizeColorOptionList(
        (Array.isArray(values) ? values : [])
            .map((item) => String(item ?? '').trim())
            .filter(Boolean)
            .map((item) => ({value: item, label: item}))
    );
}

function toStorageSelectMultiValue(values) {
    return normalizeStorageOptionList(
        (Array.isArray(values) ? values : [])
            .map((item) => {
                const parsed = parseStorageValue(item);
                return parsed ? toStorageOption(parsed) : null;
            })
            .filter(Boolean)
    );
}

function updateMultiSelectOptions(previous, nextValue, actionMeta, normalizer) {
    const prev = Array.isArray(previous) ? previous : [];

    let candidate;
    if ((actionMeta?.action === 'create-option' || actionMeta?.action === 'select-option') && actionMeta?.option) {
        const fromNext = Array.isArray(nextValue) ? nextValue : [];
        candidate = [...prev, ...fromNext, actionMeta.option];
    } else if (Array.isArray(nextValue)) {
        candidate = nextValue;
    } else if (actionMeta?.action === 'clear' || actionMeta?.action === 'remove-value' || actionMeta?.action === 'pop-value' || actionMeta?.action === 'deselect-option') {
        candidate = [];
    } else if (nextValue && typeof nextValue === 'object') {
        candidate = [...prev, nextValue];
    } else {
        candidate = prev;
    }

    return normalizer(candidate);
}

function normalizeColorOptionList(options) {
    const unique = new Map();
    (Array.isArray(options) ? options : []).forEach((option) => {
        const value = String(option?.value ?? '').trim();
        if (!value) return;
        unique.set(value.toLowerCase(), {value, label: String(option?.label || value).trim() || value});
    });
    return Array.from(unique.values());
}

function selectValuesToStrings(options) {
    return normalizeColorOptionList(options).map((option) => option.value);
}

function normalizeStorageOptionList(options) {
    const unique = new Map();
    (Array.isArray(options) ? options : []).forEach((option) => {
        const parsed = parseStorageOptionFromOption(option);
        if (!parsed) return;
        unique.set(`${parsed.storage}_${parsed.unit}`, toStorageOption(parsed));
    });
    return Array.from(unique.values()).sort((a, b) => {
        const storageCompare = a.data.storage - b.data.storage;
        if (storageCompare !== 0) return storageCompare;
        return a.data.unit.localeCompare(b.data.unit);
    });
}

function parseStorageOptionFromOption(option) {
    if (!option) return null;
    if (typeof option === 'object') {
        const parsedFromData = parseStorageValue(option.data);
        if (parsedFromData) return parsedFromData;
        const parsedFromObject = parseStorageValue(option);
        if (parsedFromObject) return parsedFromObject;
        return parseStorageValue(option.value ?? option.label ?? null);
    }
    return parseStorageValue(option);
}

function parseStorageValue(value) {
    if (!value) return null;
    if (typeof value === 'object') {
        const storage = Number(value.storage);
        const unit = String(value.unit || '').toUpperCase();
        if (!Number.isFinite(storage) || storage <= 0) return null;
        if (unit !== 'GB' && unit !== 'TB') return null;
        return {storage: Math.round(storage), unit};
    }

    const raw = String(value).trim().toUpperCase();
    const match = raw.match(/^(\d+(?:[.,]\d+)?)\s*(?:\|\s*)?(GB|TB)$/i);
    if (!match) return null;
    const storage = Number(String(match[1]).replace(',', '.'));
    const unit = match[2].toUpperCase();
    if (!Number.isFinite(storage) || storage <= 0) return null;
    return {storage: Math.round(storage), unit};
}

function toStorageOption({storage, unit}) {
    const normalized = {storage: Math.round(storage), unit: String(unit).toUpperCase()};
    return {
        value: `${normalized.storage}|${normalized.unit}`,
        label: `${normalized.storage} ${normalized.unit}`,
        data: normalized
    };
}

function selectValuesToStorageOptions(options) {
    return normalizeStorageOptionList(options).map((option) => ({
        storage: option.data.storage,
        unit: option.data.unit
    }));
}

function normalizeBuybackParts(value) {
    if (!Array.isArray(value)) return [];
    const normalized = value
        .flatMap((item) => {
            if (typeof item === 'string' || item?._id) {
                return [String(item?._id || item)];
            }
            if (item && typeof item === 'object' && Array.isArray(item.options)) {
                // backward compatibility with old structure
                return item.options.map((part) => String(part?._id || part));
            }
            return [];
        })
        .map((id) => String(id || '').trim())
        .filter(Boolean);
    return Array.from(new Set(normalized));
}

export default DeviceModal;
